import { hash, toUnitFloat } from "daedalus-core";
import { textDigest } from "../rules/version.ts";
import { TOOL_KINDS, type ToolKind } from "../rules/types.ts";
import { HARVEST_GROUP_ID, MAX_HARVEST_ID_CHARS, type HarvestGroup } from "./harvest.ts";

/**
 * The rolls of a harvest and the names a harvestable prop is known by (ADR-142 decision 3): what a harvest gives, how long
 * a harvested prop takes to grow back, the index a map numbers its harvestable props by and the mark the compile gives a
 * frame of a group. Every roll comes from the shared seeded hash, so the page, the map server and a tool agree on it. Pure.
 */

/**
 * The most harvestable props one map holds: the index of a prop is a two-byte number on the wire, so the 65536th
 * prop has no index to be named by.
 */
export const MAX_HARVEST_PROPS = 65_535;

/**
 * How far a character may stand from a harvestable prop's feet point and still harvest it, in sixteenths of a tile: a
 * tile and a half. The page offers its prompt inside this reach and the map server refuses outside it.
 */
export const HARVEST_REACH_SIXTEENTHS = 24;

const PERCENT = 100;
const MAX_U32 = 0xffffffff;

/**
 * The seed of every harvest roll, apart from every other roll's.
 */
const HARVEST_SEED = 0x48525653;

/**
 * Which decision of a harvest a digest settles.
 */
const ROLL_QUANTITY = 0;
const ROLL_BONUS = 1;
const ROLL_PICK = 2;
const ROLL_REGROW = 3;

/**
 * What a harvest is fixed by: the character, the prop's index on its map and how many times the prop has been
 * harvested, so a prop that grows back rolls anew.
 */
export interface HarvestKey {
  readonly character: string;
  readonly prop: number;
  readonly counter: number;
}

/**
 * What a harvest gives: the yield's units and the bonus item, null when the bonus did not fall.
 */
export interface HarvestRoll {
  readonly quantity: number;
  readonly bonus: string | null;
}

/**
 * Whether a value is a whole number within a range.
 *
 * @param value The value.
 * @param min Least allowed.
 * @param max Most allowed.
 * @returns True when it is.
 */
function wholeIn(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * Checks a roll's key fits the hash's 32-bit inputs.
 *
 * @param key The key.
 * @throws RangeError when the prop or the counter is not a whole number from 0 to 2^32 - 1.
 */
function assertKey(key: HarvestKey): void {
  if (!wholeIn(key.prop, 0, MAX_U32) || !wholeIn(key.counter, 0, MAX_U32)) {
    throw new RangeError(`a harvest key's prop ${key.prop} and counter ${key.counter} must be whole numbers from 0 to ${MAX_U32}`);
  }
}

/**
 * Rolls one harvest for one character at one prop: the yield's units from `min` to `max`, then whether the bonus
 * falls (its chance in whole percents), then which bonus item, picked by weight. Every decision is its own draw from
 * the shared seeded hash over the character, the prop's index and the prop's harvest counter, so the same key gives the
 * same harvest on the page, the map server and a tool.
 *
 * @param group A group of a table `validateHarvestTable` admitted.
 * @param key The character, the prop and how many times the prop has been harvested.
 * @returns The units of the yield and the bonus item, null for none.
 * @throws RangeError when the prop or the counter is not a whole number from 0 to 2^32 - 1.
 * @example
 * rollHarvest(grass, { character: "0192...", prop: 41, counter: 0 });
 * // { quantity: 2, bonus: "worm" } or { quantity: 1, bonus: null }
 */
export function rollHarvest(group: HarvestGroup, key: HarvestKey): HarvestRoll {
  assertKey(key);
  const character = textDigest(key.character, HARVEST_SEED);
  const unit = (decision: number): number => toUnitFloat(hash(HARVEST_SEED, character, key.prop, key.counter, decision));
  const spread = group.yield.max - group.yield.min + 1;
  const quantity = group.yield.min + Math.floor(unit(ROLL_QUANTITY) * spread);
  if (Math.floor(unit(ROLL_BONUS) * PERCENT) >= group.bonus.chance) {
    return { quantity, bonus: null };
  }
  let total = 0;
  for (const entry of group.bonus.items) {
    total += entry.weight;
  }
  let pick = Math.floor(unit(ROLL_PICK) * total);
  for (const entry of group.bonus.items) {
    if (pick < entry.weight) {
      return { quantity, bonus: entry.item };
    }
    pick -= entry.weight;
  }
  return { quantity, bonus: group.bonus.items[group.bonus.items.length - 1]?.item ?? null };
}

/**
 * The ticks a harvested prop takes to grow back: the group's regrow plus a hashed 0 to its spread, fixed by the prop's
 * index and its harvest counter alone, so every viewer's page and the map server agree and a meadow harvested at once
 * does not return at once.
 *
 * @param group A group of a table `validateHarvestTable` admitted.
 * @param prop The prop's index on its map.
 * @param counter How many times the prop has been harvested, this harvest included.
 * @returns The ticks from the harvest to the regrow.
 * @throws RangeError when the prop or the counter is not a whole number from 0 to 2^32 - 1.
 */
export function regrowTicks(group: HarvestGroup, prop: number, counter: number): number {
  assertKey({ character: "", prop, counter });
  if (group.regrowSpreadTicks === 0) {
    return group.regrowTicks;
  }
  const added = Math.floor(toUnitFloat(hash(HARVEST_SEED, prop, counter, ROLL_REGROW)) * (group.regrowSpreadTicks + 1));
  return group.regrowTicks + added;
}

/**
 * The order a map's harvestable props are numbered in: their authored ids sorted by UTF-16 code unit, so a prop's index
 * is its place in the list and does not move with the order the props were placed or listed in. The compile and every
 * reader of the index use this one rule.
 *
 * @param ids The authored ids of the map's harvestable props.
 * @returns The ids in index order, a fresh list.
 * @throws Error when an id is held twice or there are more props than `MAX_HARVEST_PROPS`.
 */
export function harvestPropOrder(ids: readonly string[]): string[] {
  if (ids.length > MAX_HARVEST_PROPS) {
    throw new Error(`a map holds ${ids.length} harvestable props, past the ${MAX_HARVEST_PROPS} a two-byte index names`);
  }
  const sorted = [...ids].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  for (let index = 1; index < sorted.length; index += 1) {
    if (sorted[index] === sorted[index - 1]) {
      throw new Error(`prop id ${sorted[index] as string} is held twice among a map's harvestable props`);
    }
  }
  return sorted;
}

/**
 * The mark the compile gives a frame of a group (`CompileContext.markedFrames`): the tool the page needs in hand and the
 * group the map server rolls, joined by a slash.
 *
 * @param group A group of a table `validateHarvestTable` admitted.
 * @returns `<tool>/<group id>`.
 */
export function harvestMark(group: Pick<HarvestGroup, "tool" | "id">): string {
  return `${group.tool}/${group.id}`;
}

/**
 * Reads a mark a chunk lists back into its tool and group id.
 *
 * @param mark The mark.
 * @returns The tool kind and group id, or null for a mark that is not a harvest's.
 */
export function parseHarvestMark(mark: string): { readonly tool: ToolKind; readonly group: string } | null {
  const at = mark.indexOf("/");
  if (at < 1 || mark.length > MAX_HARVEST_ID_CHARS + 16) {
    return null;
  }
  const tool = mark.slice(0, at);
  const group = mark.slice(at + 1);
  return (TOOL_KINDS as readonly string[]).includes(tool) && HARVEST_GROUP_ID.test(group) ? { tool: tool as ToolKind, group } : null;
}
